/**
 * NebulaRecommendWidget - Bubble chart for Nebula AI recommendations
 *
 * Displays BUY/SELL/HOLD recommendations in Highcharts packedbubble
 * Matching D3 BubbleChart implementation from simplize-fe
 *
 * Supports:
 * - Single result_type (paper/live/backtest)
 * - All result_type with tabs for switching view
 * - Responsive mobile design with scrollable tabs
 * - Status-based switching: RUNNING/INIT/PROCESS_EOD → progress bar with polling
 */

import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import Highcharts from 'highcharts';
import 'highcharts/modules/stock';
import 'highcharts/highcharts-more';
import { detectDarkMode, observeDarkMode, WIDGET_BACKGROUND } from './chartTheme';
import { callWidgetApi } from '../../utils/widgetApi';

const ACTION_COLORS = {
    BUY: '#25B770',
    SELL: '#E14040',
    HOLD: '#9CA3AF',
};

const TAB_LABELS = {
    backtest: 'Hiệu suất quá khứ',
    paper: 'Giao dịch giả lập',
    live: 'Dành cho bạn',
};

const PROCESSING_STATUSES = new Set(['INIT', 'RUNNING', 'PROCESS_EOD']);

const PROGRESS_MESSAGES = {
    INIT: 'Đang khởi tạo...',
    RUNNING: 'Đang xử lý dữ liệu...',
    PROCESS_EOD: 'Đang xử lý dữ liệu cuối ngày...',
};

const JOB_TYPE_MESSAGES = {
    initialize: 'Đang khởi tạo dữ liệu...',
    update: 'Đang cập nhật dữ liệu...',
    reinitialize: 'Đang khởi tạo lại dữ liệu...',
};

const INACTIVE_MESSAGES = {
    ERROR: 'Đã xảy ra lỗi, vui lòng liên hệ hỗ trợ',
    UNSUPPORTED: 'Không tìm được rổ cổ phiếu phù hợp',
    SUSPENDED: 'Tạm dừng hoạt động',
    REINIT: 'Cần khởi tạo lại',
};

const POLL_INTERVAL = 5000;
const POLL_TIMEOUT = 5 * 60 * 1000;
const MAX_RETRIES = 1;

function extractNebulaStatus(data) {
    if (data?.nebula_status) return data.nebula_status;
    const nested = data?.data;
    if (nested && typeof nested === 'object') {
        for (const key of ['paper', 'live', 'backtest']) {
            if (nested[key]?.nebula_status) return nested[key].nebula_status;
        }
    }
    return null;
}

/**
 * Calculate fixed radius scale like D3 scalePow with exponent 0.5
 */
function createRadiusScale(minValue, maxValue, minRadius, maxRadius) {
    return (value) => {
        const absValue = Math.abs(value);
        if (minValue === maxValue) return (minRadius + maxRadius) / 2;
        const normalized = Math.sqrt((absValue - minValue) / (maxValue - minValue));
        const radius = minRadius + normalized * (maxRadius - minRadius);
        return Math.max(minRadius, Math.min(maxRadius, radius));
    };
}

export default function NebulaRecommendWidget({ data: initialData, config }) {
    const [data, setData] = useState(initialData);
    const [isDarkMode, setIsDarkMode] = useState(detectDarkMode);
    const [activeTab, setActiveTab] = useState('backtest');
    const [containerWidth, setContainerWidth] = useState(400);
    const [progress, setProgress] = useState(0);
    const [jobMessage, setJobMessage] = useState('');
    const [timedOut, setTimedOut] = useState(false);
    const retryCountRef = useRef(0);
    const chartRef = useRef(null);
    const containerRef = useRef(null);
    const chartInstance = useRef(null);

    const nebulaStatus = extractNebulaStatus(data);
    const isProcessing = PROCESSING_STATUSES.has(nebulaStatus);

    useEffect(() => {
        if (!containerRef.current) return;
        const resizeObserver = new ResizeObserver((entries) => {
            for (const entry of entries) setContainerWidth(entry.contentRect.width);
        });
        resizeObserver.observe(containerRef.current);
        setContainerWidth(containerRef.current.offsetWidth);
        return () => resizeObserver.disconnect();
    }, []);

    const isMobile = containerWidth < 400;
    const isCompact = containerWidth < 300;

    useEffect(() => observeDarkMode(setIsDarkMode), []);

    useEffect(() => {
        if (!isProcessing || timedOut) return;

        const controller = new AbortController();
        const jobType = config?.params?.job_type || 'initialize';
        const startTime = Date.now();
        let stopped = false;

        const fetchStatus = async () => {
            if (stopped) return;

            if (Date.now() - startTime >= POLL_TIMEOUT) {
                stopped = true;
                clearInterval(pollId);
                setTimedOut(true);
                return;
            }

            try {
                const result = await callWidgetApi(
                    'get_nebula_job_status', { job_type: jobType }, controller.signal
                );
                const jobData = result?.data;
                if (!jobData) return;

                const pct = jobData.progress ?? 0;
                setProgress(prev => Math.max(prev, Math.min(99, pct)));
                if (jobData.message) setJobMessage(jobData.message);

                if (jobData.job_status !== 'RUNNING') {
                    stopped = true;
                    clearInterval(pollId);
                    setProgress(100);
                    try {
                        const stats = await callWidgetApi(
                            'get_nebula_stats', config?.params || {}, controller.signal
                        );
                        if (stats?.data) {
                            setTimeout(() => setData(stats.data), 600);
                        }
                    } catch { /* ignore */ }
                }
            } catch {
                /* ignore abort/network errors */
            }
        };

        fetchStatus();
        const pollId = setInterval(fetchStatus, POLL_INTERVAL);

        return () => {
            stopped = true;
            controller.abort();
            clearInterval(pollId);
        };
    }, [isProcessing, config, timedOut]);

    const handleRetry = useCallback(() => {
        if (retryCountRef.current >= MAX_RETRIES) return;
        retryCountRef.current += 1;
        setProgress(0);
        setJobMessage('');
        setTimedOut(false);
    }, []);

    const progressMessage = useMemo(() => {
        if (jobMessage) return jobMessage;
        const jobType = config?.params?.job_type;
        if (jobType && JOB_TYPE_MESSAGES[jobType]) return JOB_TYPE_MESSAGES[jobType];
        return PROGRESS_MESSAGES[nebulaStatus] || 'Đang xử lý...';
    }, [jobMessage, config, nebulaStatus]);

    const { isAllType, availableTabs, allData } = useMemo(() => {
        if (!data) return { isAllType: false, availableTabs: [], allData: null };
        const nestedData = data.data;
        if (nestedData && typeof nestedData === 'object' &&
            (nestedData.paper || nestedData.live || nestedData.backtest)) {
            const tabs = [];
            if (nestedData.backtest?.latest_recommendations?.length > 0) tabs.push('backtest');
            if (nestedData.paper?.latest_recommendations?.length > 0) tabs.push('paper');
            if (nestedData.live?.latest_recommendations?.length > 0) tabs.push('live');
            return { isAllType: true, availableTabs: tabs, allData: nestedData };
        }
        return { isAllType: false, availableTabs: [], allData: null };
    }, [data]);

    useEffect(() => {
        if (isAllType && availableTabs.length > 0 && !availableTabs.includes(activeTab)) {
            setActiveTab(availableTabs[0]);
        }
    }, [isAllType, availableTabs, activeTab]);

    const recommendations = useMemo(() => {
        if (!data) return [];
        if (isAllType && allData) {
            const tabData = allData[activeTab];
            return tabData?.latest_recommendations || [];
        }
        let recs = [];
        if (Array.isArray(data)) {
            recs = data;
        } else if (data.latest_recommendations) {
            recs = data.latest_recommendations;
        } else if (data.data?.latest_recommendations) {
            recs = data.data.latest_recommendations;
        }
        return recs;
    }, [data, isAllType, allData, activeTab]);

    const chartHeight = useMemo(() => {
        if (isCompact) return 200;
        if (isMobile) return 250;
        return Math.min(400, containerWidth * 0.8);
    }, [containerWidth, isMobile, isCompact]);

    const { valueRange, radiusSettings } = useMemo(() => {
        const nonZeroQuantities = recommendations
            .filter(r => r.quantity !== 0)
            .map(r => Math.abs(r.quantity));

        const minQty = nonZeroQuantities.length > 0 ? Math.min(...nonZeroQuantities) : 1;
        const maxQty = nonZeroQuantities.length > 0 ? Math.max(...nonZeroQuantities) : 100;
        const allZero = recommendations.every(r => r.quantity === 0);

        const baseMin = isCompact ? 20 : isMobile ? 25 : 40;
        const baseMax = isCompact ? 40 : isMobile ? 50 : 80;

        const settings = allZero
            ? {
                normal: { min: baseMin, max: baseMax },
                zero: { min: baseMin, max: baseMax },
              }
            : {
                normal: { min: baseMin, max: baseMax },
                zero: { min: baseMin * 0.5, max: baseMax * 0.6 },
              };

        return {
            valueRange: { minQty, maxQty, range: maxQty - minQty },
            radiusSettings: settings,
        };
    }, [recommendations, isMobile, isCompact]);

    const randZeroRad = useMemo(() => {
        const res = [];
        const base = radiusSettings.zero.max - radiusSettings.zero.min;
        const min = radiusSettings.zero.min;
        recommendations.forEach((rec) => {
            if (rec.quantity === 0) {
                res.push(Math.random() * base + min);
            }
        });
        return res;
    }, [recommendations, radiusSettings]);

    const radiusScale = useMemo(() => {
        return createRadiusScale(
            valueRange.minQty,
            valueRange.maxQty,
            radiusSettings.normal.min,
            radiusSettings.normal.max
        );
    }, [valueRange, radiusSettings]);

    const textScaleFactor = useMemo(() => {
        if (isCompact) return 0.6;
        if (isMobile) return 0.75;
        return 1;
    }, [isMobile, isCompact]);

    const seriesData = useMemo(() => {
        const grouped = { BUY: [], SELL: [], HOLD: [] };
        let zeroIndex = 0;

        recommendations.forEach(rec => {
            let action = (rec.action || 'HOLD').toString().toUpperCase();
            const quantity = rec.quantity || 0;
            if (quantity > 0 && action === 'HOLD') action = 'BUY';
            else if (quantity < 0 && action === 'HOLD') action = 'SELL';

            const actionLabel = action === 'BUY' ? 'Mua' : action === 'SELL' ? 'Bán' : 'Giữ';
            const absQuantity = Math.abs(quantity);

            let radius;
            let opacity;

            if (quantity !== 0) {
                radius = radiusScale(absQuantity);
                opacity = valueRange.range > 0
                    ? (absQuantity / valueRange.range) * 0.6 + 0.4
                    : 1.0;
            } else {
                radius = randZeroRad[zeroIndex] || radiusSettings.zero.min;
                zeroIndex++;
                opacity = 1.0;
            }

            if (grouped[action]) {
                grouped[action].push({
                    name: `${rec.ticker}`,
                    ticker: rec.ticker,
                    action: actionLabel,
                    quantity: quantity,
                    value: radius * radius,
                    radius: radius,
                    opacity: opacity,
                });
            }
        });

        return [
            {
                name: 'Mua',
                color: ACTION_COLORS.BUY,
                data: grouped.BUY,
                marker: {
                    lineWidth: 0,
                    fillColor: {
                        radialGradient: { cx: 0.5, cy: 0.5, r: 0.5 },
                        stops: [
                            [0.8, 'rgba(37, 183, 112, 0.3)'],
                            [1, 'rgba(37, 183, 112, 1)']
                        ]
                    }
                }
            },
            {
                name: 'Bán',
                color: ACTION_COLORS.SELL,
                data: grouped.SELL,
                marker: {
                    lineWidth: 0,
                    fillColor: {
                        radialGradient: { cx: 0.5, cy: 0.5, r: 0.5 },
                        stops: [
                            [0.8, 'rgba(225, 64, 64, 0.3)'],
                            [1, 'rgba(225, 64, 64, 1)']
                        ]
                    }
                }
            },
            {
                name: 'Không giao dịch',
                color: ACTION_COLORS.HOLD,
                data: grouped.HOLD,
                marker: {
                    lineWidth: 0,
                    fillColor: {
                        radialGradient: { cx: 0.5, cy: 0.5, r: 0.5 },
                        stops: [
                            [0.75, 'rgba(156, 163, 175, 0.2)'],
                            [1, 'rgba(156, 163, 175, 1)']
                        ]
                    }
                }
            },
        ].filter(series => series.data.length > 0);
    }, [recommendations, valueRange, radiusSettings, radiusScale, randZeroRad]);

    useEffect(() => {
        if (!chartRef.current || seriesData.length === 0) return;

        const options = {
            chart: {
                type: 'packedbubble',
                height: chartHeight,
                backgroundColor: 'transparent',
                events: {
                    render: function() {
                        const chart = this;
                        chart.series.forEach(series => {
                            series.points.forEach(point => {
                                if (point.graphic && point.opacity !== undefined) {
                                    point.graphic.attr({ opacity: point.opacity });
                                }
                            });
                        });
                    }
                }
            },
            title: { text: null },
            tooltip: {
                useHTML: true,
                pointFormat: '<b>{point.name}</b><br/>{point.action}: {point.quantity}',
            },
            plotOptions: {
                packedbubble: {
                    minSize: '20%',
                    maxSize: '100%',
                    layoutAlgorithm: {
                        splitSeries: false,
                        gravitationalConstant: 0.02,
                        seriesInteraction: true,
                        dragBetweenSeries: true,
                        parentNodeLimit: true,
                        parentNodeOptions: { bubbleMargin: 1 }
                    },
                    dataLabels: {
                        enabled: true,
                        useHTML: true,
                        allowOverlap: true,
                        formatter: function() {
                            const logoUrl = `https://cdn.simplize.vn/simplizevn/logo/${this.point.ticker}.jpeg`;
                            const showQuantity = this.point.action !== 'Giữ';
                            const radius = this.point.radius || 50;

                            const allRadii = this.series.chart.series.flatMap(s =>
                                s.data.map(d => d.radius || 50)
                            );
                            const minR = Math.min(...allRadii);
                            const maxR = Math.max(...allRadii);

                            const normalized = (radius - minR) / (maxR - minR || 1);
                            const rScale = 0.7 + normalized * 0.5;
                            const scale = rScale * textScaleFactor;

                            const logoSize = Math.round(24 * scale);
                            const tickerSize = Math.round(16 * scale);
                            const actionSize = Math.round(12 * scale);

                            const showLogo = radius >= (isCompact ? 25 : isMobile ? 30 : 35);

                            return `<div style="display:flex;flex-direction:column;align-items:center;gap:1px;">
${showLogo ? `<div style="width:${logoSize}px;height:${logoSize}px;border-radius:50%;overflow:hidden;background:#f3f4f6;flex-shrink:0;">
<img src="${logoUrl}" style="width:100%;height:100%;object-fit:contain;" onerror="this.style.opacity='0'" />
</div>` : ''}
<div style="font-family:Inter,sans-serif;font-weight:700;font-size:${tickerSize}px;line-height:1.2;color:#1f2937;text-align:center;">
${this.point.ticker}
</div>
${showQuantity ? `<div style="font-family:Inter,sans-serif;font-weight:400;font-size:${actionSize}px;line-height:1.2;color:#6b7280;text-align:center;">
${this.point.action}: ${Math.abs(this.point.quantity)}
</div>` : ''}</div>`;
                        },
                        style: { textOutline: 'none' },
                    },
                },
            },
            series: seriesData,
            credits: { enabled: false },
            legend: { enabled: false },
        };

        chartInstance.current = Highcharts.chart(chartRef.current, options);

        return () => {
            if (chartInstance.current) {
                chartInstance.current.destroy();
                chartInstance.current = null;
            }
        };
    }, [seriesData, isDarkMode, chartHeight, textScaleFactor, isMobile, isCompact]);

    // --- Render: Processing state → progress bar ---
    if (isProcessing) {
        return (
            <div ref={containerRef}
                 className="border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden"
                 style={{ backgroundColor: isDarkMode ? WIDGET_BACKGROUND.dark : WIDGET_BACKGROUND.light }}>
                <div className="flex flex-col items-center justify-center py-12 px-6">
                    {timedOut ? (
                        retryCountRef.current < MAX_RETRIES ? (
                            <>
                                <div className="text-sm text-gray-500 dark:text-gray-400 mb-4 text-center">
                                    Quá trình xử lý chưa hoàn tất
                                </div>
                                <button
                                    onClick={handleRetry}
                                    className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors"
                                    style={{ background: 'linear-gradient(to right, #EC8000, #006CEC)' }}>
                                    Thử lại
                                </button>
                            </>
                        ) : (
                            <div className="text-sm text-gray-500 dark:text-gray-400 text-center">
                                Quá trình xử lý mất nhiều thời gian hơn dự kiến, vui lòng quay lại sau
                            </div>
                        )
                    ) : (
                        <>
                            <div className="text-sm font-medium mb-4" style={{ color: '#EC8000' }}>
                                Loading ({Math.round(progress)}%)…
                            </div>
                            <div className="w-full max-w-[464px] h-6 rounded-2xl overflow-hidden"
                                 style={{ backgroundColor: isDarkMode ? '#2a2a2a' : '#e5e7eb', padding: 3 }}>
                                <div className="h-full rounded-2xl"
                                     style={{
                                         width: `${progress}%`,
                                         background: 'linear-gradient(to right, #EC8000, #FAD9B3, #006CEC)',
                                         transition: 'width 420ms ease',
                                     }} />
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400 mt-4 text-center font-medium">
                                {progressMessage}
                            </div>
                        </>
                    )}
                </div>
            </div>
        );
    }

    // --- Render: Inactive statuses ---
    if (nebulaStatus && nebulaStatus !== 'ACTIVE' && INACTIVE_MESSAGES[nebulaStatus]) {
        return (
            <div ref={containerRef}
                 className="border border-gray-100 dark:border-gray-800 rounded-xl p-6 text-center text-gray-500"
                 style={{ backgroundColor: isDarkMode ? WIDGET_BACKGROUND.dark : WIDGET_BACKGROUND.light }}>
                {INACTIVE_MESSAGES[nebulaStatus]}
            </div>
        );
    }

    // --- Render: ACTIVE or no status → bubble chart ---
    if (recommendations.length === 0 && !isAllType) {
        return (
            <div ref={containerRef}
                 className="border border-gray-100 dark:border-gray-800 rounded-xl p-6 text-center text-gray-500"
                 style={{ backgroundColor: isDarkMode ? WIDGET_BACKGROUND.dark : WIDGET_BACKGROUND.light }}>
                Không có khuyến nghị giao dịch
            </div>
        );
    }

    if (isAllType && availableTabs.length === 0) {
        return (
            <div ref={containerRef}
                 className="border border-gray-100 dark:border-gray-800 rounded-xl p-6 text-center text-gray-500"
                 style={{ backgroundColor: isDarkMode ? WIDGET_BACKGROUND.dark : WIDGET_BACKGROUND.light }}>
                Không có khuyến nghị giao dịch
            </div>
        );
    }

    return (
        <div ref={containerRef}
             className="border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden"
             style={{ backgroundColor: isDarkMode ? WIDGET_BACKGROUND.dark : WIDGET_BACKGROUND.light }}>
            {isAllType && availableTabs.length > 1 && (
                <div className="overflow-x-auto scrollbar-hide">
                    <div className="flex items-center justify-center gap-2 px-4 py-3 min-w-max">
                        {availableTabs.map((tab) => (
                            <button
                                key={tab}
                                onClick={() => setActiveTab(tab)}
                                className={`px-3 py-1.5 text-xs sm:text-sm font-medium transition-all rounded-full whitespace-nowrap ${
                                    activeTab === tab
                                        ? 'bg-blue-600 text-white shadow-sm'
                                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                                }`}
                            >
                                {TAB_LABELS[tab] || tab}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {recommendations.length > 0 ? (
                <div ref={chartRef} className="w-full" />
            ) : (
                <div className="p-6 text-center text-gray-500 text-sm">
                    Không có khuyến nghị giao dịch cho {TAB_LABELS[activeTab] || activeTab}
                </div>
            )}

            {recommendations.length > 0 && (
                <div className={`px-3 pb-2 flex flex-wrap items-center justify-center gap-2 sm:gap-3 ${isCompact ? 'text-xs' : 'text-sm'}`}>
                    <div className="flex items-center gap-1.5">
                        <span className={`${isCompact ? 'w-3 h-3' : 'w-4 h-4'} rounded-full`} style={{ backgroundColor: ACTION_COLORS.BUY }}></span>
                        <span className="text-gray-700 dark:text-gray-300">Mua</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <span className={`${isCompact ? 'w-3 h-3' : 'w-4 h-4'} rounded-full`} style={{ backgroundColor: ACTION_COLORS.SELL }}></span>
                        <span className="text-gray-700 dark:text-gray-300">Bán</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <span className={`${isCompact ? 'w-3 h-3' : 'w-4 h-4'} rounded-full`} style={{ backgroundColor: ACTION_COLORS.HOLD }}></span>
                        <span className="text-gray-700 dark:text-gray-300">Không giao dịch</span>
                    </div>
                </div>
            )}
        </div>
    );
}
